// Relationship changes affect web queries whose keys differ from the native app.
// Keep this registry next to the mutation callers rather than duplicating native keys.
import { syncMemberMemory } from './memberMemory';
const ownedRoots = new Set([
  'web-discovery',
  'web-members',
  'web-following',
  'web-search-bridge',
  'web-search-results',
  'web-search-category-page',
]);
const sharedRoots = new Set(['user', 'followers', 'following', 'home', 'search']);
const safetyRoots = new Set([
  'contentSafety',
  'publicMusicRecords',
  'musicRecordsSearch',
  'web-member-reviews',
  'web-reviewer-candidates',
  'web-divided-album',
  'browse-reviews',
  'browse-playlists',
  'browse-curator',
  'albumReviews',
  'reviewComments',
  'playlistComments',
  'memberReviews',
]);
const same = (a, b) => a != null && b != null && String(a) === String(b);
export function affectedMemberQuery(key, viewer, safety = false) {
  const [root, second] = key;
  if (ownedRoots.has(root)) return same(second, viewer.id);
  if (root === 'users') return second !== 'discover' || same(key[2], viewer.id);
  if (sharedRoots.has(root)) return true;
  if (!safety) return false;
  if (root === 'web-parity') return ['explore', 'recent-reviews'].includes(second);
  if (root === 'review') return same(key[2], viewer.id);
  if (root === 'playlist') return second === 'detail' && same(key[3], viewer.id);
  return safetyRoots.has(root);
}
export async function syncMemberChange(client, { viewer, target, kind, current }) {
  if (!viewer?.id || !current()) return false;
  const safety = kind !== 'follow';
  const filters = { predicate: (q) => affectedMemberQuery(q.queryKey, viewer, safety) };
  // Cancel reads begun before the mutation so they cannot overwrite the new state.
  await client.cancelQueries(filters);
  if (!current()) return false;
  syncMemberMemory({ viewer, target, kind });
  if (safety) {
    // Never render pre-block content during a refetch or after a failed refetch.
    // Search terms, route and scroll memory are separate and remain intact.
    void client.resetQueries(filters);
  } else {
    client.setQueriesData({ queryKey: ['users', 'discover', viewer.id] }, (data) =>
      data
        ? {
            ...data,
            pages: data.pages.map((page) =>
              page.map((item) => (same(item.id, target.id) ? { ...item, ...target } : item)),
            ),
          }
        : data,
    );
    client.setQueriesData({ queryKey: ['web-following', viewer.id] }, (data) => {
      if (!Array.isArray(data)) return data;
      const next = data.filter((item) => !same(item.id, target.id));
      return target.isFollowing ? [...next, target] : next;
    });
    // Do not replace loaded discovery pages as a user returns from a profile.
    void client.invalidateQueries({ ...filters, refetchType: 'none' });
    void client.refetchQueries({
      predicate: (q) =>
        filters.predicate(q) && !(q.queryKey[0] === 'users' && q.queryKey[1] === 'discover'),
      type: 'active',
    });
  }
  return true;
}
